import { useCallback, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { ActiveUser, useRealtimeCollaboration } from './useRealtimeCollaboration';

interface UseRepairOrderViewersOptions {
  /**
   * The recno of the repair order currently open in the drawer. Pass null
   * when no repair order is open. Changes here drive the broadcast
   * lifecycle: a new recno triggers a viewing_started broadcast and cleanup
   * fires viewing_stopped — so closing the drawer, switching to a different
   * repair order, and unmounting are all covered.
   */
  activeRepairOrderId: number | string | null;
}

interface UseRepairOrderViewersReturn {
  /** Map of repair order recno (stringified) -> the active viewers for it. */
  repairOrderViewers: Record<string, ActiveUser[]>;
}

/**
 * Tracks which users are currently viewing each repair order. All callers
 * share a single global "repair-order-viewers" room so users on different
 * host pages can still see each other when looking at the same repair order.
 *
 * Consumers receive the raw viewers map; the rendering site is responsible
 * for filtering self / deduping by user_id when displaying the list.
 */
export function useRepairOrderViewers({
  activeRepairOrderId,
}: UseRepairOrderViewersOptions): UseRepairOrderViewersReturn {
  const [repairOrderViewers, setRepairOrderViewers] = useState<Record<string, ActiveUser[]>>({});
  const { accounts } = useMsal();
  // Pull stable primitives out of currentUser so the effect below doesn't
  // re-fire on every render just because MSAL handed us a new object ref —
  // that would cause a stop/start broadcast flicker for other viewers.
  const userName = accounts[0]?.name || accounts[0]?.username || 'Anonymous';
  const userId = accounts[0]?.localAccountId || accounts[0]?.homeAccountId || 'unknown';
  const userEmail = accounts[0]?.username;

  const handleLiveUpdate = useCallback((update: { update_type: string; payload: any }) => {
    if (update.update_type === 'repair_order_viewing_started') {
      const { repairOrderId, viewer } = update.payload;
      setRepairOrderViewers((prev) => ({
        ...prev,
        [repairOrderId]: [...(prev[repairOrderId] || []), viewer],
      }));
    } else if (update.update_type === 'repair_order_viewing_stopped') {
      const { repairOrderId, viewerId } = update.payload;
      setRepairOrderViewers((prev) => ({
        ...prev,
        [repairOrderId]: (prev[repairOrderId] || []).filter((v) => v.sid !== viewerId),
      }));
    }
  }, []);

  const { broadcastUpdate, isConnected, socket } = useRealtimeCollaboration({
    pageId: 'repair-order-viewers',
    userName,
    userId,
    userEmail,
    onLiveUpdate: handleLiveUpdate,
  });

  useEffect(() => {
    if (!isConnected || !socket?.id || !activeRepairOrderId) return;

    const recnoKey = activeRepairOrderId.toString();
    const sid = socket.id;
    const viewer: ActiveUser = {
      sid,
      user_name: userName,
      user_id: userId,
      user_email: userEmail,
    };

    broadcastUpdate('repair_order_viewing_started', { repairOrderId: recnoKey, viewer });
    setRepairOrderViewers((prev) => ({
      ...prev,
      [recnoKey]: [...(prev[recnoKey] || []), viewer],
    }));

    return () => {
      broadcastUpdate('repair_order_viewing_stopped', { repairOrderId: recnoKey, viewerId: sid });
      setRepairOrderViewers((prev) => ({
        ...prev,
        [recnoKey]: (prev[recnoKey] || []).filter((v) => v.sid !== sid),
      }));
    };
  }, [activeRepairOrderId, isConnected, socket?.id, userName, userId, userEmail, broadcastUpdate]);

  return { repairOrderViewers };
}
